import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Icon, Input, Modal } from "semantic-ui-react";

import { NoteTypeIcon, useCan } from "components";
import { APPOINTMENT_NOTE_TYPES, type Appointment, type AppointmentNote } from "models";
import { AppointmentNoteModal } from "modules/AppointmentDetails/components";
import { CustomerCommunicationInterventions } from "modules/AppointmentDetails/components/CustomerCommunicationModal/components/CustomerCommunicationInterventions";
import "modules/AppointmentDetails/components/DeskCommunication/components/DeskCommunicationModal/DeskCommunicationModal.scss";
import { useCustomerWaitingHandleMutation } from "modules/AppointmentDetails/components/DeskCommunication/hooks";
import { isCarReadyOrQualityCheckDoneOrCanceled } from "utils/appointmentUtils";

type DeskCommunicationModalProps = {
  appointment: Appointment;
  onClose: () => void;
  onSend: () => void;
};

const DESK_NOTE_TYPES = {
  Parking: APPOINTMENT_NOTE_TYPES.ParkingLocation,
  Wo: APPOINTMENT_NOTE_TYPES.WO,
  Main: APPOINTMENT_NOTE_TYPES.Main,
  TemporaryDriverNote: APPOINTMENT_NOTE_TYPES.TempDriver
};

export const DeskCommunicationModal = ({ appointment, onClose, onSend }: DeskCommunicationModalProps) => {
  const [customerWaiting, setCustomerWaiting] = useState<boolean>(appointment.customer_waiting);
  const [selectedNote, setSelectedNote] = useState<AppointmentNote | null>(null);
  const [noteType, setNoteType] = useState<number | null>(null);
  const [woNoteId, setWoNoteId] = useState<number | null>(null);
  const [temporaryDriverNoteId, setTemporaryDriverNoteId] = useState<number | null>(null);

  const { t } = useTranslation();
  const { mutate: customerWaitingMutation, isPending } = useCustomerWaitingHandleMutation({
    appointment_id: appointment.id,
    customer_waiting: customerWaiting
  });

  const handleSelectNote = (appointmentNoteTypeId: number | null) => {
    if (!appointmentNoteTypeId) return;

    const foundNote = appointment.notes?.find(note => note.appointment_note_type_id === appointmentNoteTypeId);
    if (foundNote && [DESK_NOTE_TYPES.Parking, DESK_NOTE_TYPES.Main].includes(foundNote.appointment_note_type_id)) setSelectedNote(foundNote);

    setNoteType(appointmentNoteTypeId);
  };

  const closeNoteModal = () => {
    setNoteType(null);
    setSelectedNote(null);
  };

  const handleSaveNote = (appointmentNoteTypeId: number, id: number) => {
    if (appointmentNoteTypeId === DESK_NOTE_TYPES.Wo) setWoNoteId(id);
    if (appointmentNoteTypeId === DESK_NOTE_TYPES.TemporaryDriverNote) setTemporaryDriverNoteId(id);
  };

  const handleCustomerWaitingChange = () => setCustomerWaiting(prev => !prev);

  const handleSendDeskComm = () => {
    if (customerWaiting !== appointment.customer_waiting) customerWaitingMutation();
    onSend();
  };

  const isCarReadyOrQualityCheckDone = isCarReadyOrQualityCheckDoneOrCanceled(appointment.appointment_status_identifier);

  const canCreateNote = {
    [DESK_NOTE_TYPES.Parking]: useCan(["create", "update"], "appointment-notes-parking-location") && !isCarReadyOrQualityCheckDone,
    [DESK_NOTE_TYPES.Main]: useCan(["create", "update"], "appointment-notes") && !isCarReadyOrQualityCheckDone,
    [DESK_NOTE_TYPES.Wo]: useCan("create", "appointment-notes-wo") && !isCarReadyOrQualityCheckDone,
    [DESK_NOTE_TYPES.TemporaryDriverNote]: useCan("create", "appointment-notes-temp-driver") && !isCarReadyOrQualityCheckDone
  };

  const getNoteValue = (noteType: APPOINTMENT_NOTE_TYPES) => {
    const note = appointment.notes?.find(n => {
      if (noteType === DESK_NOTE_TYPES.Wo) return n.id === woNoteId;
      if (noteType === DESK_NOTE_TYPES.TemporaryDriverNote) return n.id === temporaryDriverNoteId;
      return n.appointment_note_type_id === noteType;
    });

    return note?.note || "";
  };

  const getPlaceholderValue = (noteType: APPOINTMENT_NOTE_TYPES) => {
    switch (noteType) {
      case DESK_NOTE_TYPES.Main:
        return t("v8_main_note", "Main note");
      case DESK_NOTE_TYPES.Parking:
        return t("v8_parking_note", "Parking note");
      case DESK_NOTE_TYPES.TemporaryDriverNote:
        return t("v8_temporary_driver_note", "Temporary Driver Note");
      case DESK_NOTE_TYPES.Wo:
      default:
        return t("v8_note", "Note");
    }
  };

  return (
    <Modal open className="DeskCommunicationModal" size="fullscreen" dimmer="blurring" onClose={onClose}>
      <Modal.Header className="DeskCommunicationModal__header">
        {isCarReadyOrQualityCheckDone ? t("v8_desk_check_out", "Desk Check-out") : t("v8_desk_check_in", "Desk Check-in")}

        <Button className="-appointment-status" size="small" onClick={onClose}>
          <Icon className="xmark grey" size="big" />
        </Button>
      </Modal.Header>
      <Modal.Content>
        <div className="DeskCommunicationModal__content">
          <div className="notes-container">
            {Object.values(DESK_NOTE_TYPES).map(noteType => (
              <div key={noteType} className="notes-container-input">
                <NoteTypeIcon type={noteType} />
                <div className="clickable-input-wrapper">
                  <Input
                    placeholder={getPlaceholderValue(noteType)}
                    value={getNoteValue(noteType)}
                    onClick={() => handleSelectNote(noteType)}
                    readOnly
                    disabled={!canCreateNote[noteType]}
                  />
                </div>
              </div>
            ))}

            <div className="notes-container-checkbox">
              <label>
                <Icon className="mug saucer" />
                {t("v8_customer_waiting", "Customer Waiting")}
              </label>

              <Checkbox checked={customerWaiting} onChange={handleCustomerWaitingChange} disabled={isCarReadyOrQualityCheckDone} />
            </div>
          </div>

          <div className="interventions-container">
            <CustomerCommunicationInterventions appointment={appointment} showCustomerOk />
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions className="DeskCommunicationModal__actions">
        <Button loading={isPending} disabled={isPending} color="green" className="save-btn" onClick={handleSendDeskComm}>
          {t("v8_send", "Send")}
          <Icon className="check" />
        </Button>
      </Modal.Actions>

      {noteType && (
        <AppointmentNoteModal
          selectedAppointmentNote={selectedNote}
          createAppointmentNoteType={noteType}
          onUnselectNote={closeNoteModal}
          appointment={appointment}
          onSave={handleSaveNote}
        />
      )}
    </Modal>
  );
};
